import {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { isRecord } from "utils/isRecord";

import type { StreamEvent } from "../../../../stream/streamIterator";
import type { ScoutConversationPrivateDocumentRow } from "./useScoutConversationPrivateDocuments";

import { useToken } from "../../../../providers/token";
import { StreamObserver } from "../../../../stream/observer";
import { useOakStreamClient } from "../../../../stream/useOakStreamClient";
import { isScoutOptimisticConversationId } from "./scoutConversationIds";
import { conversationNeedsPrivateDocumentStream } from "./scoutPrivateDocumentQuery";

export function buildScoutPrivateDocumentStreamUrl(input: {
  tenantId: string | undefined;
  conversationId: string | undefined;
}): string {
  if (!input.tenantId || !input.conversationId) {
    return "/events/scout/private-documents/disabled";
  }

  return `/events/${input.tenantId}/scout/${input.conversationId}/private-documents`;
}

function readStreamEventData(event: StreamEvent): Record<string, unknown> | undefined {
  if (!isRecord(event.data)) {
    return undefined;
  }

  return event.data;
}

function mapStreamEventToDocumentRow(
  data: Record<string, unknown>,
  existing?: ScoutConversationPrivateDocumentRow
): ScoutConversationPrivateDocumentRow | undefined {
  const privateDocumentId =
    typeof data.privateDocumentId === "string" ? data.privateDocumentId : undefined;
  const extractionStatusRaw =
    typeof data.extractionStatus === "string" ? data.extractionStatus : undefined;

  if (!privateDocumentId || !extractionStatusRaw) {
    return undefined;
  }

  const extractionStatus = extractionStatusRaw as ScoutPrivateDocumentExtractionStatus;
  const extractionProgressRaw = data.extractionProgress;
  let extractionProgress: ScoutConversationPrivateDocumentRow["extractionProgress"] =
    existing?.extractionProgress ?? null;

  if (extractionProgressRaw === null) {
    extractionProgress = null;
  } else if (isRecord(extractionProgressRaw)) {
    const pagesCompleted = extractionProgressRaw.pagesCompleted;
    const pagesTotal = extractionProgressRaw.pagesTotal;
    if (typeof pagesCompleted === "number") {
      extractionProgress = {
        pagesCompleted,
        pagesTotal:
          typeof pagesTotal === "number" ? pagesTotal : pagesTotal === null ? null : undefined,
      };
    }
  }

  const pageCountRaw = data.pageCount;
  let pageCount = existing?.pageCount;
  if (pageCountRaw === null) {
    pageCount = null;
  } else if (typeof pageCountRaw === "number") {
    pageCount = pageCountRaw;
  }

  return {
    id: privateDocumentId,
    filename: existing?.filename ?? "Document",
    sizeBytes: existing?.sizeBytes,
    pageCount,
    ingestionMode: existing?.ingestionMode ?? ScoutPrivateDocumentIngestionMode.LargeFileExtraction,
    extractionStatus,
    previewSummary:
      typeof data.previewSummary === "string"
        ? data.previewSummary
        : data.previewSummary === null
          ? null
          : existing?.previewSummary,
    fileExtension: existing?.fileExtension,
    mediaType: existing?.mediaType,
    signedDownloadUrl: existing?.signedDownloadUrl,
    extractionProgress,
  };
}

/**
 * Subscribes to the conversation private-document SSE stream while large PDF
 * work is in flight and invokes onDocumentUpdates for each event.
 */
export function useScoutPrivateDocumentStream(input: {
  tenantId: string | undefined;
  conversationId: string | undefined;
  /** Wait until the initial GQL hydrate finishes so the API snapshot sees DB rows. */
  privateDocumentsHydrated: boolean;
  documents: ScoutConversationPrivateDocumentRow[];
  onDocumentUpdates: (updates: ScoutConversationPrivateDocumentRow[]) => void;
}): void {
  const streamClient = useOakStreamClient();
  const { getIdTokenRefreshIfExpired } = useToken();
  const onDocumentUpdatesRef = useRef(input.onDocumentUpdates);
  onDocumentUpdatesRef.current = input.onDocumentUpdates;
  const documentsRef = useRef(input.documents);
  documentsRef.current = input.documents;
  const shouldConnect = useMemo(
    () => input.privateDocumentsHydrated && conversationNeedsPrivateDocumentStream(input.documents),
    [input.documents, input.privateDocumentsHydrated]
  );

  const handleStreamEvent = useCallback((event: StreamEvent) => {
    if (event.event !== "private_document_updated") {
      return;
    }

    const data = readStreamEventData(event);
    if (!data) {
      return;
    }

    const privateDocumentId =
      typeof data.privateDocumentId === "string" ? data.privateDocumentId : undefined;
    if (!privateDocumentId) {
      return;
    }

    const existing = documentsRef.current.find((row) => row.id === privateDocumentId);
    const mapped = mapStreamEventToDocumentRow(data, existing);
    if (!mapped) {
      return;
    }

    onDocumentUpdatesRef.current([mapped]);
  }, []);

  useEffect(() => {
    if (
      !streamClient ||
      !shouldConnect ||
      !input.tenantId ||
      !input.conversationId ||
      isScoutOptimisticConversationId(input.conversationId)
    ) {
      return;
    }

    const url = buildScoutPrivateDocumentStreamUrl({
      tenantId: input.tenantId,
      conversationId: input.conversationId,
    });

    const observer = new StreamObserver(streamClient, {
      enabled: true,
      url,
      getIdTokenRefreshIfExpired,
    });

    observer.mount();

    let cancelled = false;
    const processedEventIds = new Set<string>();

    const processEvents = () => {
      if (cancelled) {
        return;
      }

      const result = observer.getCurrentResult();
      for (const event of result.data) {
        if (processedEventIds.has(event.id)) {
          continue;
        }

        processedEventIds.add(event.id);
        handleStreamEvent(event);
      }
    };

    const unsubscribe = observer.subscribe(() => {
      processEvents();
    });

    void observer.connect().then(() => {
      processEvents();
    });

    return () => {
      cancelled = true;
      unsubscribe();
      observer.destroy();
    };
  }, [
    getIdTokenRefreshIfExpired,
    handleStreamEvent,
    input.conversationId,
    input.privateDocumentsHydrated,
    input.tenantId,
    shouldConnect,
    streamClient,
  ]);
}
